import React from "react";
import PropTypes from "prop-types";
import "./IntervalPlot.scss";

/**
 * IntervalPlot — groups of DIFFERENT SIZES compared fairly: each group's rate as a dot, with
 * the range that rate could lie in by chance alone drawn through it.
 *
 * Why not bars of counts: a group of 1,500 will always have more of anything than a group of
 * 1,000, and a bar of the rates alone hides how little a small group's rate is worth ("15%"
 * from 3 of 20 could as easily be 5% or 36%). Here every row shares one axis, so two groups
 * differ only if their ranges do not overlap, and the eye can see that without a statistic.
 *
 * The count the rate rests on is always printed beside it. A group marked `small` is drawn
 * faint, and one marked `counting` (still collecting) is drawn hollow.
 *
 * ⚠️ `estimate` is a MODE for figures that are estimates, not counts. In it a row prints its
 * rounded RANGE as the figure (`row.figure`, e.g. "20% to 75%") with the word "estimate"
 * beneath; it never prints the middle as a number, and never "k of n": an estimate drawn as
 * "39% · 9,046 of 22,910" reads as a count (the independent review of PR #61). A row that has
 * no estimate says why (`row.none`). Without `estimate` the chart is exactly as it was.
 *
 * @param rows       [{ key, label, k, n, rate, low, high, small?, counting?, sub? }] — order is the caller's
 *                   in estimate mode: [{ key, label, rate, low, high, figure, small?, none? }]
 * @param reference  optional { value, label } — a dashed line, e.g. the rate for all groups together
 * @param max        optional top of the axis (a rate, 0–1); otherwise fitted to the widest range
 */
const pct = (v) => {
  if (v == null || Number.isNaN(v)) return "—";
  const x = v * 100;
  if (x === 0) return "0%";
  return x < 1 ? `${x.toFixed(1)}%` : x < 10 ? `${x.toFixed(1).replace(/\.0$/, "")}%` : `${Math.round(x)}%`;
};
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-GB"));

/** A round top for the axis: the first of these that clears the widest range. */
const TOPS = [0.005, 0.01, 0.02, 0.03, 0.05, 0.08, 0.1, 0.15, 0.2, 0.25, 0.3, 0.4, 0.5, 0.75, 1];
export const axisTop = (rows, reference) => {
  const widest = Math.max(0, ...rows.map((r) => r.high || r.rate || 0), reference && reference.value ? reference.value : 0);
  return TOPS.find((t) => t >= widest * 1.08) || 1;
};

const IntervalPlot = ({ rows, reference, max, color, unit = "", of = "", emptyText = "Nothing to compare yet.", activeKeys = [], estimate = false }) => {
  if (!rows || !rows.length) return <p className="interval-plot__empty">{emptyText}</p>;
  const top = max || axisTop(rows, reference);
  const x = (v) => `${Math.min(Math.max((v || 0) / top, 0), 1) * 100}%`;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * top);
  return (
    <div className="interval-plot" style={color ? { "--interval-color": color } : undefined}>
      <div className="interval-plot__row interval-plot__row--axis" aria-hidden="true">
        <span />
        <span className="interval-plot__track interval-plot__track--axis">
          {ticks.map((t) => <i key={t} style={{ left: x(t) }}>{pct(t)}</i>)}
        </span>
        <span />
      </div>
      {rows.map((r) => {
        const cls = ["interval-plot__row", r.small ? "interval-plot__row--small" : "", r.counting ? "interval-plot__row--counting" : "",
          activeKeys.includes(r.key) ? "interval-plot__row--apart" : ""].filter(Boolean).join(" ");
        const counted = r.rate == null ? `${r.label}: no figure` : `${r.label}: ${pct(r.rate)}, ${fmt(r.k)} of ${fmt(r.n)}${of ? ` ${of}` : ""}; could be ${pct(r.low)} to ${pct(r.high)}`
          + `${r.small ? "; too few to trust" : ""}${r.counting ? "; still counting" : ""}`;
        const estimated = r.rate == null || !r.figure ? `${r.label}: ${r.none || "no estimate"}` : `${r.label}: estimate ${r.figure}${r.small ? "; very low confidence" : ""}`;
        const said = estimate ? estimated : counted;
        return (
          <div className={cls} key={r.key} role="img" aria-label={said} title={said}>
            <span className="interval-plot__label">
              {r.label}
              {r.sub && <small>{r.sub}</small>}
            </span>
            <span className="interval-plot__track">
              {ticks.slice(1, -1).map((t) => <b key={t} style={{ left: x(t) }} />)}
              {reference && reference.value != null && <u style={{ left: x(reference.value) }} />}
              {r.rate != null && (!estimate || r.figure) && (
                <>
                  <span className="interval-plot__range" style={{ left: x(r.low), width: `calc(${x(r.high)} - ${x(r.low)})` }} />
                  <span className="interval-plot__dot" style={{ left: x(r.rate) }} />
                </>
              )}
            </span>
            {estimate ? (
              <span className="interval-plot__figure interval-plot__figure--estimate">
                {r.rate == null || !r.figure ? <small>{r.none || "no estimate"}</small> : <><strong>{r.figure}</strong><small>estimate</small></>}
              </span>
            ) : (
              <span className="interval-plot__figure">
                <strong>{pct(r.rate)}</strong>
                <small>{fmt(r.k)} of {fmt(r.n)}{unit ? ` ${unit}` : ""}</small>
              </span>
            )}
          </div>
        );
      })}
      {reference && reference.value != null && (
        <p className="interval-plot__key"><u /> {reference.label}: {pct(reference.value)}</p>
      )}
    </div>
  );
};

IntervalPlot.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    k: PropTypes.number,
    n: PropTypes.number,
    rate: PropTypes.number,
    low: PropTypes.number,
    high: PropTypes.number,
    small: PropTypes.bool,
    counting: PropTypes.bool,
    sub: PropTypes.node,
    /** Estimate mode: the rounded range to print as the figure, e.g. "20% to 75%". */
    figure: PropTypes.string,
    /** Estimate mode: why this row has no estimate. */
    none: PropTypes.string,
  })).isRequired,
  reference: PropTypes.shape({ value: PropTypes.number, label: PropTypes.string }),
  max: PropTypes.number,
  color: PropTypes.string,
  /** After the count: "3 of 20 {unit}". */
  unit: PropTypes.string,
  /** What the rate is a share of, for the spoken description: "emails delivered". */
  of: PropTypes.string,
  emptyText: PropTypes.string,
  /** Rows to mark as standing apart from the rest. */
  activeKeys: PropTypes.arrayOf(PropTypes.string),
  /** The rows are ESTIMATES: print each one's rounded range, never its middle or "k of n". */
  estimate: PropTypes.bool,
};

export default IntervalPlot;
